import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth } from "../firebase";
import { storage } from "../firebase";
import { compressImageFile } from "./compressImage";

/**
 * Uploads a file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} folderPath - The folder path where the file should be stored (e.g., "Profile_Images", "Inventory_Images", "Lead_Images")
 * @param {string} businessName - The business name (will be sanitized and used as primary folder)
 * @returns {Promise<string|null>} - The download URL of the uploaded file, or null if upload failed
 */
export const uploadToFirebaseStorage = async (file, folderPath, businessName) => {
    if (!file) {
        return null;
    }

    if (!businessName) {
        return null;
    }

    // Check if user is authenticated (optional for Lead_Images uploads from public forms)
    const currentUser = auth.currentUser;
    if (!currentUser && folderPath !== "Lead_Images") {
        throw new Error("Authentication required. Please sign in to upload files.");
    }

    try {
        let fileToUpload = file;
        if (folderPath === "Inventory_Images" || folderPath.startsWith("Themes/")) {
            const maxEdge = folderPath === "Inventory_Images" ? 800 : 1600;
            const quality = folderPath === "Inventory_Images" ? 0.75 : 0.82;
            fileToUpload = await compressImageFile(file, { maxEdge, quality });
        }

        // Sanitize business name for use in file paths
        const safeBusinessName = businessName.replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
        
        // Sanitize filename to remove special characters
        const sanitizedFileName = fileToUpload.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        
        // Generate a unique filename with timestamp
        const timestamp = Date.now();
        const fileName = `${timestamp}_${sanitizedFileName}`;
        
        // Construct the full storage path: {businessName}/{folderPath}/{fileName}
        // Same pattern as Profile_Images and Inventory_Images
        const storagePath = `${safeBusinessName}/${folderPath}/${fileName}`;
        const storageRef = ref(storage, storagePath);

        // Upload the file
        await uploadBytes(storageRef, fileToUpload);

        // Get the download URL
        const downloadURL = await getDownloadURL(storageRef);
        
        return downloadURL;
    } catch (error) {
        // Provide more specific error messages
        if (error.code === 'storage/unauthorized') {
            throw new Error("You don't have permission to upload files. Please check your Firebase Storage security rules.");
        } else if (error.code === 'storage/canceled') {
            throw new Error("Upload was canceled.");
        } else if (error.code === 'storage/unknown') {
            throw new Error("An unknown error occurred. Please check your Firebase Storage configuration and security rules.");
        } else {
            throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
        }
    }
};

/**
 * Helper function to get business name from userData
 * @param {Object} userData - User data object
 * @returns {string} - Business name or "default" if not available
 */
export const getBusinessName = (userData) => {
    if (!userData) return "default";
    
    // Try businessInfo.businessName first, then businessName, then fallback to default
    return userData.businessInfo?.businessName || 
           userData.businessName || 
           "default";
};

